import { LitElement, html, css } from 'lit-all'
import {
  StorefrontClient,
  getStampedRatingData,
  renderStampedRatingBadge,
} from 'lib-utils'
import * as paths from 'lib-paths'
import styles from 'lib-styles'

class NovaMiniCartRecommendedProducts extends LitElement {
  static properties = {
    items: { type: Array, state: true },
    title: { type: String },
    cartData: { type: Object },
    productColours: { type: Object, state: true },
    ratingData: { type: Array },
  }

  constructor() {
    super()
    this.compatibleProducts = []
    this.cartData = {}
    this.client = new StorefrontClient()
    this.productColours = {}
    this.styles = {
      disabled: {
        backgroundColor: '#D9DBDA',
        textColor: '#4b4b4b',
      },
      enabled: {
        backgroundColor: '#5812CD',
        textColor: '#ffffff',
      },
    }
    this.ratingData = []
  }

  connectedCallback() {
    super.connectedCallback()
  }

  async updated(changedProperties) {
    if (changedProperties.has('items')) {
      this.handleCompatibleProducts(this.items)
      this.ratingData = await getStampedRatingData(this.compatibleProducts)
    }
  }

  async handleCompatibleProducts(items) {
    if (items.length < 1) return
    const compatibleProducts = []
    const productIds = new Set()
    const allFetchedProducts = []

    const fetchPromises = items.map((item) =>
      this.client
        .getUpsellProductsById(item.product_id, this.cartData.country_code)
        .then((response) => {
          const upsellProduct = response.metafields?.custom?.up_sell
          if (!upsellProduct) return
          const newProducts = upsellProduct.filter(
            (product) => !productIds.has(product.id)
          )
          allFetchedProducts.push(...newProducts)
          if (items.length === 1) {
            for (const product of newProducts.slice(0, 4)) {
              productIds.add(product.id)
              compatibleProducts.push(product)
            }
          } else {
            let count = 0
            for (const product of newProducts) {
              if (!productIds.has(product.id) && count < 2) {
                productIds.add(product.id)
                compatibleProducts.push(product)
                count++
              }
              if (count >= 2) break
            }
          }
        })
        .catch((e) => {
          console.error(e)
        })
    )
    await Promise.all(fetchPromises)

    if (compatibleProducts.length < 4) {
      const additionNeeded = 4 - compatibleProducts.length

      const remainingProducts = allFetchedProducts.filter(
        (product) => !productIds.has(product.id)
      )

      for (let i = 0; i < additionNeeded && i < remainingProducts.length; i++) {
        compatibleProducts.push(remainingProducts[i])
        productIds.add(remainingProducts[i].id)
      }
    }

    this.compatibleProducts = compatibleProducts
    this.loadColours(compatibleProducts)
    this.refineProductData(this.compatibleProducts)
    this.requestUpdate()
    this.handleButtonStyles()
    this.observeInertAttributes()
  }

  observeInertAttributes = () => {
    const carousels = this.shadowRoot.querySelectorAll('sl-carousel')
    for (const carousel of carousels) {
      const carouselItems = carousel.querySelectorAll('sl-carousel-item')
      for (const item of carouselItems) {
        item.removeAttribute('inert')

        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (
              mutation.type === 'attributes' &&
              mutation.attributeName === 'inert'
            ) {
              item.removeAttribute('inert')
            }
          }
        })
        observer.observe(item, { attributes: true })
      }
    }
  }

  refineProductData(products) {
    for (const product of products) {
      product.price *= 100
      product.compare_at_price *= 100
      product.compareAtPrice *= 100
      product.minPrice *= 100
      product.maxPrice *= 100
      for (const variant of product.variants) {
        variant.price *= 100
        variant.compare_at_price *= 100
        variant.compareAtPrice *= 100
      }
    }
  }

  getSwatchFiles(productId) {
    // return object where key is colour name and value is colour file
    return this.productColours[productId]
      ? this.productColours[productId].colourFiles
      : {}
  }

  getSwatchValues(product) {
    let swatchValues = []
    const appearColorOptionNames = []

    for (const option of product.options) {
      const optionName = option.name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '')

      if (
        [
          'color',
          'colour',
          'sex-toy-color',
          'sex-toy-colour',
          'harness-color',
          'harness-colour',
        ].includes(optionName)
      ) {
        swatchValues = swatchValues.concat(option.values)

        if (!appearColorOptionNames.includes(optionName)) {
          appearColorOptionNames.push(optionName)
        }
      }
    }

    if (appearColorOptionNames.length > 1) {
      swatchValues = null
    }

    return swatchValues
  }

  getSizeValues(product) {
    let sizeValues = []
    const appearSizeOptionNames = []

    for (const option of product.options) {
      const optionName = option.name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '')

      if (['size', 'sex-toy-size', 'harness-size'].includes(optionName)) {
        sizeValues = sizeValues.concat(option.values)

        if (!appearSizeOptionNames.includes(optionName)) {
          appearSizeOptionNames.push(optionName)
        }
      }
    }

    if (appearSizeOptionNames.length > 1) {
      sizeValues = null
    }

    return sizeValues
  }

  async _fetchData(productId) {
    return await this.client.getProductById(productId)
  }

  async loadColours(products) {
    for (const data of products) {
      if (data != null) {
        const colourOptions = data.options.find(
          (option) =>
            option.name.toLowerCase() === 'color' ||
            option.name.toLowerCase() === 'colour'
        )
        const newObjEntry = {}

        const colourFiles = {}
        for (const variant of data.variants) {
          if (variant.selectedOptions && variant.selectedOptions.length > 0) {
            const colourOption = variant.selectedOptions.find(
              (option) =>
                option.name.toLowerCase() === 'color' ||
                option.name.toLowerCase() === 'colour'
            )
            if (colourOption) {
              const colorSwatchHandle = colourOption.value
                .toLowerCase()
                .replace(/ /g, '-')
              const colorSwatchFile =
                variant.metafields['as-wfh']?.color_swatch_file?.image.url
              if (colorSwatchFile)
                colourFiles[colorSwatchHandle] = colorSwatchFile
            }
          }
        }

        if (colourOptions != null) {
          newObjEntry[data.id] = {
            coloursArray: colourOptions.values,
            colourFiles: colourFiles,
          }
          this.productColours = { ...this.productColours, ...newObjEntry }
        }
      }
    }
  }

  handleButtonStyles = () => {
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const carousel = this.shadowRoot.querySelector(
            '.js-recommended-products__carousel'
          )
          if (carousel) {
            const carouselObserver = new MutationObserver(() => {
              const buttons = carousel.shadowRoot.querySelectorAll('button')
              for (const button of buttons) {
                this.observeButtonClassChanges(button)
                this.updateButtonStyles(button)
              }
            })
            carouselObserver.observe(carousel.shadowRoot, {
              childList: true,
              subtree: true,
            })
            observer.disconnect()
          }
        }
      }
    })

    observer.observe(this.shadowRoot, { childList: true, subtree: true })
  }

  observeButtonClassChanges(button) {
    const classObserver = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          this.updateButtonStyles(button)
        }
      }
    })

    classObserver.observe(button, { attributes: true })
  }

  updateButtonStyles(button) {
    button.style.opacity = 1
    const slot = button.querySelector('slot')
    const assignedNodes = slot.assignedNodes({ flatten: true })
    const isDisabled = button.classList.contains(
      'carousel__navigation-button--disabled'
    )
    const { backgroundColor, textColor } = isDisabled
      ? this.styles.disabled
      : this.styles.enabled

    for (const node of assignedNodes) {
      node.style.backgroundColor = backgroundColor
      for (const path of node.querySelectorAll('path')) {
        path.style.stroke = textColor
      }
    }
  }

  stripAndTruncateHTML(htmlString, wordLimit) {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = htmlString
    const textContent = tempDiv.textContent || tempDiv.innerText || ''
    const words = textContent.split(' ')
    if (words.length > wordLimit) {
      return `${words.slice(0, wordLimit).join(' ')}...`
    }
    return textContent
  }

  getBagdes(product) {
    if (!product.metafields || product.metafields.length === 0) return
    const productBadges = product.metafields['as-wfh']?.product_badges
    return productBadges
  }

  render() {
    if (this.compatibleProducts.length === 0) return html``

    return html`
      <div class="recommended-products__container flex flex-column">
        <div class="recommended-products__title flex">
          <h3 class="h5">${this.title}</h3>
        </div>
        <sl-carousel
          class="recommended-products__carousel js-recommended-products__carousel"
          navigation
          slides-per-page="2.3"
          style="--aspect-ratio: auto;"
        >
          ${this.compatibleProducts.map(
            (product) =>
              html` <sl-carousel-item
                class="recommended-products__carousel-item"
              >
                <nova-product-card
                  id="${product.id}"
                  handle="${product.handle}"
                  url="/products/${product.handle}"
                  available="${product.available}"
                  title="${product.title}"
                  image="${product.featured_image}"
                  secondaryImage="${product.images[1]?.src}"
                  description="${this.stripAndTruncateHTML(
                    product.description,
                    15
                  )}"
                  ${product.compare_at_price_max !== '0.00' &&
                  product.price < product.compare_at_price_max
                    ? html`compare-at-price=${product.compare_at_price}`
                    : null}
                  price="${product.price}"
                  vendor="${product.vendor}"
                  productCardType="mini"
                  .badges="${this.getBagdes(product)}"
                  .colourSwatchesFiles="${this.getSwatchFiles(product.id)}"
                  .variants="${product.variants}"
                  .swatches="${this.getSwatchValues(product)}"
                  .sizes="${this.getSizeValues(product)}"
                >
                  <!-- Stamped - Begin Star Rating Badge -->
                  <span
                    class="stamped-product-reviews-badge stamped-main-badge"
                    data-id="${product.id}"
                    data-product-sku="${product.handle}"
                    data-product-type="${product.type}"
                    data-product-title="${product.title}"
                    style="display:block;"
                    slot="stamped-badge"
                  >
                    ${renderStampedRatingBadge(this.ratingData, product.id)}
                  </span>
                  <!-- Stamped - End Star Rating Badge -->

                  <!-- Wishlist King - Begin Wishlist Button -->
                  <wishlist-button-collection
                    data-product-handle="${product.handle}"
                    show-icon
                    icon="wishlist"
                    align-self="center"
                    align-content="center"
                    slot="wishlist-button"
                  ></wishlist-button-collection>
                  <!-- Wishlist King - End Wishlist Button -->
                </nova-product-card>
              </sl-carousel-item>`
          )}
          <button
            slot="previous-icon"
            class="recommended-products__carousel-button"
            aria-label="Previous button"
          >
            ${paths.arrowShaftLeft}
          </button>
          <button
            slot="next-icon"
            class="recommended-products__carousel-button"
            aria-label="Next button"
          >
            ${paths.arrowShaftRight}
          </button>
        </sl-carousel>
      </div>
    `
  }

  static styles = [
    styles.flex,
    styles.typography,
    styles.wishListKing,
    styles.stamped,
    css`
      .recommended-products__container {
        padding: var(--nova-space-2xl, 24px) var(--nova-space-xl, 20px);
        background-color: var(--nova-color-light, #f5f5f5);
      }

      .recommended-products__title {
        height: 44px;
        align-items: flex-end;
      }

      h3 {
        color: var(--nova-brand-primary, #5812CD);
        margin: var(--nova-space-none, 0);
      }

      sl-carousel::part(base) {
        grid-template-areas:
          '. navigation .'
          '. slides .';
        grid-column-gap: var(--nova-space-none, 0);
        grid-row-gap: var(--nova-space-lg, 16px);
        margin-top: -44px;
      }

      sl-carousel::part(navigation) {
        display: flex;
        justify-content: flex-end;
        gap: var(--nova-space-2xs, 4px);
      }

      sl-carousel::part(navigation-button) {
        padding: var(--nova-space-none, 0);
      }

      .recommended-products__carousel-button {
        display: flex;
        justify-content: center;
        align-items: center;
        border: none;
        height: 44px;
        width: 44px;
        background-color: var(--nova-brand-primary, #5812CD);
        border-radius: var(--nova-border-radius-m, 6px);
        cursor: pointer;
      }

      .recommended-products__carousel-item,
      nova-product-card {
        height: 100%;
        background-color: var(--nova-brand-secondary, #ffffff);
        border-radius: var(--nova-border-radius-l, 12px);
      }

      .recommended-products__carousel-item {
        padding: var(--nova-space-xs, 8px);
      }
    `,
  ]
}
customElements.define(
  'nova-mini-cart-recommended-products',
  NovaMiniCartRecommendedProducts
)
